import { Badge } from "@/components/ui/badge";
import { PendingScore } from "./PendingScore";
import { LEVEL_BADGE_CLASSES, LEVEL_LABELS } from "@/lib/level-badge";
import { scoreRisk } from "@shared/scoring/risk-matrix";
import { resultRowLabel, type AssetHazardResult } from "@/lib/results";
import type { Asset } from "@/lib/assets";

const EXPOSURE_LEVELS = [4, 3, 2, 1] as const;
const VULNERABILITY_LEVELS = [1, 2, 3, 4] as const;
const TOOLTIP_MAX_ENTRIES = 10;

interface RiskMatrixPanelProps {
  results: AssetHazardResult[];
  assets: Asset[];
}

interface CellEntry {
  assetName: string;
  hazardLabel: string;
}

function buildTooltip(entries: CellEntry[]): string {
  const lines = entries.slice(0, TOOLTIP_MAX_ENTRIES).map((e) => `${e.assetName} — ${e.hazardLabel}`);
  if (entries.length > TOOLTIP_MAX_ENTRIES) lines.push(`+${entries.length - TOOLTIP_MAX_ENTRIES} more`);
  return lines.join("\n");
}

/** The 4x4 grid itself is static/known (functional spec §2.1) — it always
 * renders. Rows/columns are labeled with this app's Low/Medium/High/Very
 * High scale (LEVEL_LABELS — same wording as the map/badges/gauges)
 * instead of raw "Exp 1"/"Vuln 4", since the bare numeric scale isn't
 * self-explanatory on its own. Each cell shows a real asset×hazard count,
 * colored by that (exposure, vulnerability) pair's resulting risk level
 * (scoreRisk()), once /api/results has computed rows for it; PendingScore
 * ("—") for any cell still empty. Hovering a populated cell lists which
 * asset×hazard combinations it contains — a cell is a count, not a single
 * asset, so this is how you trace a number back to real sites. */
export function RiskMatrixPanel({ results, assets }: RiskMatrixPanelProps) {
  const assetNameById = new Map(assets.map((a) => [a.id, a.name]));

  const cellEntries = new Map<string, CellEntry[]>();
  for (const r of results) {
    if (r.status !== "computed" || r.exposure === undefined || r.vulnerability === undefined) continue;
    const key = `${r.exposure}:${r.vulnerability}`;
    const entry: CellEntry = {
      assetName: assetNameById.get(r.assetId) ?? "Unknown asset",
      hazardLabel: resultRowLabel(r),
    };
    const existing = cellEntries.get(key);
    if (existing) existing.push(entry);
    else cellEntries.set(key, [entry]);
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="mb-3 text-sm font-semibold">Risk matrix — Exposure × Vulnerability</p>
      <table className="w-full text-center text-xs">
        <thead>
          <tr>
            <th className="p-1.5 text-left text-[10px] font-normal text-muted-foreground">
              Exposure ↓<br />
              Vulnerability →
            </th>
            {VULNERABILITY_LEVELS.map((v) => (
              <th key={v} className="p-1.5 font-medium text-muted-foreground">
                {LEVEL_LABELS[v]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {EXPOSURE_LEVELS.map((e) => (
            <tr key={e}>
              <td className="p-1.5 text-left font-medium text-muted-foreground">{LEVEL_LABELS[e]}</td>
              {VULNERABILITY_LEVELS.map((v) => {
                const entries = cellEntries.get(`${e}:${v}`);
                const risk = scoreRisk(e, v);
                return (
                  <td key={v} className="p-1.5">
                    {entries ? (
                      <Badge className={LEVEL_BADGE_CLASSES[risk]} title={buildTooltip(entries)}>
                        {entries.length}
                      </Badge>
                    ) : (
                      <PendingScore label="—" />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
